import type { FeedItem } from '~/hooks/queries/use-feed';

/**
 * FRONT-1251: tell the assistant which scheduled job produced the inbox item
 * the user is looking at.
 *
 * A chat opened on an inbox item hands Eddy the item's title and body as
 * context (see `assetContext` in `inbox-chat-modal.tsx`) — and nothing else.
 * So the daily news briefing arrived, the user asked "which job made this?" or
 * "when does it run again?" or "change this schedule", and Eddy could only see
 * the prose. It had no job id to look up and no schedule to quote, and had to
 * ask the user to explain where their own inbox item came from.
 *
 * The backend now stamps a cron-run feed item with its `source` (job id and
 * name, the job's current schedule, the skill it runs, this run's id and
 * time). This turns that into the lines the model reads. Pure so the wording
 * is testable without mounting the modal.
 *
 * Returns null when there is nothing to say — a document, an uploaded file, or
 * an anchor-less chat — so the caller can drop it from the context entirely
 * rather than sending an empty heading.
 */
export interface SourceNoteOptions {
  /**
   * Whether this channel can actually change a schedule. Typed chat can reach
   * the cron tools; the voice channel's tool whitelist has no cron tool at all
   * (writes go through a delegation), so promising "I can pause that for you"
   * there is how a realtime model ends up confidently reporting a change it
   * never made.
   */
  canManageSchedule: boolean;
}

/** Job names are free text with no newline restriction. Flatten them so a
 *  name can't fabricate extra lines inside a block the model reads as
 *  instructions — org-shared job names reach every member's feed. */
function oneLine(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

export function describeFeedItemSource(
  item: Pick<FeedItem, 'source'>,
  opts: SourceNoteOptions,
): string | null {
  const source = item.source;
  if (!source || source.kind !== 'cron') return null;

  const lines = [
    'This item was produced by a scheduled job, not written by the user:',
    `- Job name: ${oneLine(source.jobName)}`,
    `- Job id: ${source.jobId}`,
  ];
  // "unknown" is describeSchedule's floor for a spec it can't read — saying
  // that out loud is worse than saying nothing.
  if (source.schedule && source.schedule !== 'unknown') {
    lines.push(`- Runs: ${source.schedule}`);
  }
  // Past tense on purpose: the skill comes from THIS run's snapshot, while the
  // schedule above is the job's current one. A job edited since this run may
  // no longer use it.
  if (source.skillSlug) lines.push(`- Skill this run used: ${source.skillSlug}`);
  lines.push(`- This run started: ${source.ranAt}`);
  if (source.runId != null) {
    // Named explicitly because the only `runId` on the tool surface is
    // skill_status's, which takes a string id of a different shape entirely —
    // an unlabelled integer here invites a confident call that returns nothing.
    lines.push(`- Inbox entry number: ${source.runId} (internal; no tool accepts it)`);
  }
  lines.push(
    opts.canManageSchedule
      ? 'Use the job id with your scheduling tools when the user asks about the job itself — which job produced this, when it runs next, or to change, pause or delete its schedule. Never ask the user where this item came from.'
      : 'Use these facts to answer questions about the job itself — which job produced this, and when it runs. Never ask the user where this item came from. Changing the schedule is not something you can do on this channel; hand that off rather than claiming it is done.',
  );
  return lines.join('\n');
}
